import { useEffect, useMemo } from "react";
import { last, isEmpty, findIndex, nth, flatMap, findLast } from "lodash-es";
import { SessionItem, SessionItemKind } from "@/mds/components/sessions/session-item";
import { useMDSSession } from "@/mds/components/sessions/session-context";
import {
  SessionEventKind,
  SessionGroupKind,
  GROUPED_SESSION_EVENT_KINDS,
  AnySessionPart,
} from "@/mds/components/sessions/session.types";
import { resolveSessionPartId } from "@/mds/components/sessions/session-context/slices/events/mds-session-context.events.utils";

export type SessionGroupInfo = {
  groupKind: SessionGroupKind;
  info: {
    isFirst: boolean;
    isLast: boolean;
  };
};

export const useEscapeKeyHandler = (isArtifactOpen: boolean, closeArtifact: () => void) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeArtifact();
      }
    };

    if (isArtifactOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isArtifactOpen, closeArtifact]);
};

/**
 * Determines if a reasoning group should be auto-collapsed based on session state changes
 */
export const getReasoningGroupToAutoCollapse = ({
  prevItems,
  currentItems,
  reasoningGroups,
}: {
  prevItems: SessionItem[];
  currentItems: SessionItem[];
  reasoningGroups: Record<string, { userToggled?: boolean; isCollapsed?: boolean }>;
}): string | null => {
  if (isEmpty(prevItems)) return null;

  const prevLastItem = last(prevItems);
  if (prevLastItem?.kind !== SessionItemKind.ReasoningGroup) return null;

  const prevGroupId = prevLastItem.id;
  const currentLastItem = last(currentItems);
  const isStillLast = currentLastItem?.id === prevGroupId;
  if (isStillLast) return null;

  const groupState = reasoningGroups[prevGroupId];
  if (groupState?.userToggled || groupState?.isCollapsed) return null;

  return prevGroupId;
};

export const useMDSSessionItem = ({ itemId }: { itemId: string }) => {
  const { internalSessionItems } = useMDSSession();

  return useMemo(() => {
    return internalSessionItems.find(item => item.id === itemId);
  }, [internalSessionItems, itemId]);
};

/**
 * Utility function to get the group kind for a given event kind
 */
const resolveSessionGroupKind = ({
  eventKind,
}: {
  eventKind: SessionEventKind;
}): SessionGroupKind | null => {
  for (const [groupKind, eventKinds] of Object.entries(GROUPED_SESSION_EVENT_KINDS)) {
    if (eventKinds.includes(eventKind)) {
      return groupKind as SessionGroupKind;
    }
  }
  return null;
};

/**
 * Shared logic for creating flat items with group kinds
 */
const useFlatItemsWithGroupKinds = ({ items }: { items: SessionItem[] }) => {
  return useMemo(() => {
    return flatMap(items, item =>
      item.data.map(itemData => ({
        itemData,
        groupKind: resolveSessionGroupKind({ eventKind: itemData.context.eventKind }),
      }))
    );
  }, [items]);
};

/**
 * Simple helper to check position of a part within its group.
 * A group is consecutive items with the same SessionGroupKind (User/Agent/System).
 */
const useSessionGroupInfoForPart = ({
  part,
  items,
}: {
  part: AnySessionPart;
  items: SessionItem[];
}) => {
  const flatItems = useFlatItemsWithGroupKinds({ items });

  return useMemo(() => {
    const targetPartId = resolveSessionPartId({
      part,
    });

    // Find our target
    const targetIndex = findIndex(flatItems, item => {
      const partId = resolveSessionPartId({
        part: item.itemData.part,
      });

      return partId === targetPartId;
    });

    // If we didn't find the target, bail out
    if (targetIndex < 0) {
      return null;
    }

    const targetEventKind = flatItems[targetIndex].itemData.context.eventKind;

    const targetGroupKind = resolveSessionGroupKind({
      eventKind: targetEventKind,
    });

    if (!targetGroupKind) {
      return null;
    }

    // Get neighboring items
    const previousItem = nth(flatItems, targetIndex - 1);
    const nextItem = nth(flatItems, targetIndex + 1);
    const isFirstItem = targetIndex === 0;
    const isLastItem = targetIndex === flatItems.length - 1;

    // Check group boundaries
    const isFirst = isFirstItem || previousItem?.groupKind !== targetGroupKind;
    const isLast = isLastItem || nextItem?.groupKind !== targetGroupKind;

    return { isFirst, isLast };
  }, [flatItems, part]);
};

/**
 * Hook to check position of an item within its groups.
 * Returns an array of group information since items can contain parts from multiple groups.
 */
export const useSessionGroupInfoForItem = ({
  itemId,
  items,
}: {
  itemId: string;
  items: SessionItem[];
}): SessionGroupInfo[] => {
  const flatItems = useFlatItemsWithGroupKinds({ items });

  return useMemo(() => {
    const item = items.find(sessionItem => sessionItem.id === itemId);

    if (!item) {
      return [];
    }

    const groupInfoMap = new Map<SessionGroupKind, { isFirst: boolean; isLast: boolean }>();

    for (const itemData of item.data) {
      const targetPartId = resolveSessionPartId({
        part: itemData.part,
      });

      // Find this part in flat items
      const targetIndex = findIndex(flatItems, flatItem => {
        const partId = resolveSessionPartId({
          part: flatItem.itemData.part,
        });
        return partId === targetPartId;
      });

      if (targetIndex < 0) continue;

      const targetGroupKind = resolveSessionGroupKind({
        eventKind: itemData.context.eventKind,
      });

      if (!targetGroupKind) continue;

      // Get neighboring items
      const previousItem = nth(flatItems, targetIndex - 1);
      const nextItem = nth(flatItems, targetIndex + 1);
      const isFirstItem = targetIndex === 0;
      const isLastItem = targetIndex === flatItems.length - 1;

      // Check group boundaries for this part
      const partIsFirst = isFirstItem || previousItem?.groupKind !== targetGroupKind;
      const partIsLast = isLastItem || nextItem?.groupKind !== targetGroupKind;

      // Update or create group info
      const existingInfo = groupInfoMap.get(targetGroupKind);
      groupInfoMap.set(targetGroupKind, {
        isFirst: existingInfo?.isFirst || partIsFirst,
        isLast: existingInfo?.isLast || partIsLast,
      });
    }

    // Convert map to array format
    return Array.from(groupInfoMap.entries()).map(([groupKind, info]) => ({
      groupKind,
      info,
    }));
  }, [items, flatItems, itemId]);
};

/**
 * Hook to determine if a session item data is the first in its group.
 */
export const useIsFirstPartInSessionGroup = ({ part }: { part: AnySessionPart }) => {
  const { internalSessionItems } = useMDSSession();
  const position = useSessionGroupInfoForPart({ part, items: internalSessionItems });
  return position?.isFirst ?? false;
};

/**
 * Hook to determine if a session item data is the last in its group.
 */
export const useIsLastPartInSessionGroup = ({ part }: { part: AnySessionPart }) => {
  const { internalSessionItems } = useMDSSession();
  const position = useSessionGroupInfoForPart({ part, items: internalSessionItems });
  return position?.isLast ?? false;
};

/**
 * Hook to determine if a session item is the first in any of its groups.
 */
export const useIsFirstItemInSessionGroup = ({ itemId }: { itemId: string }) => {
  const { internalSessionItems } = useMDSSession();
  const groupsInfo = useSessionGroupInfoForItem({ itemId, items: internalSessionItems });
  return groupsInfo.some(group => group.info.isFirst);
};

/**
 * Hook to determine if a session item is the last in any of its groups.
 */
export const useIsLastItemInSessionGroup = ({ itemId }: { itemId: string }) => {
  const { internalSessionItems } = useMDSSession();
  const groupsInfo = useSessionGroupInfoForItem({ itemId, items: internalSessionItems });
  return groupsInfo.some(group => group.info.isLast);
};

export const useIsLastItemInSessionHistory = ({ itemId }: { itemId: string }): boolean => {
  const { internalSessionItems } = useMDSSession();

  const lastItem = last(internalSessionItems);
  const lastItemData = last(lastItem?.data);
  const lastEventId = lastItemData?.context.eventId;

  if (!lastEventId) {
    return false;
  }

  const targetItem = internalSessionItems.find(item => item.id === itemId);

  return targetItem?.data.some(data => data.context.eventId === lastEventId) ?? false;
};

export const useIsLastUserMessagePartInSessionHistory = ({ part }: { part: AnySessionPart }) => {
  const { internalSessionItems } = useMDSSession();
  const flatItems = useFlatItemsWithGroupKinds({ items: internalSessionItems });

  const lastUserItem = findLast(
    flatItems,
    item => item.itemData.context.eventKind === SessionEventKind.UserActionV1
  );

  if (!lastUserItem) {
    return false;
  }

  const lastPartId = resolveSessionPartId({ part: lastUserItem.itemData.part });
  const targetPartId = resolveSessionPartId({ part });

  return lastPartId === targetPartId;
};

/**
 * Because `SessionStatus` is injected, we need to use a special utility for it.
 */
export const useIsSessionStatusFirstPartInSessionGroup = ({ part }: { part: AnySessionPart }) => {
  const { finalizedSessionItems } = useMDSSession();
  const position = useSessionGroupInfoForPart({ part, items: finalizedSessionItems });
  return position?.isFirst ?? false;
};

/**
 * Because `SessionStatus` is injected, we need to use a special utility for it.
 */
export const useIsSessionStatusLastPartInSessionGroup = ({ part }: { part: AnySessionPart }) => {
  const { finalizedSessionItems } = useMDSSession();
  const position = useSessionGroupInfoForPart({ part, items: finalizedSessionItems });
  return position?.isLast ?? false;
};
